import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import { DateCell, ListCell, MutedCell, NameCell, NumberCell } from '@/components/TableCells'
import { EarningsAccount } from '@/services/earnings/earningsAccountsApi'
import { countryName } from '@/components/CountryFilter'
import { CountryFlag } from '@/data/countries.tsx'

interface EarningsAccountTableProps {
  accounts: EarningsAccount[]
  loading?: boolean
  emptyMessage?: string
  selection?: EarningsAccount[]
  onSelectionChange?: (accounts: EarningsAccount[]) => void
}

export function EarningsAccountTable({ accounts, loading, emptyMessage, selection, onSelectionChange }: EarningsAccountTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<EarningsAccount, unknown>[] = [
    ...(selectable ? [selectionColumn<EarningsAccount>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      id: 'countries',
      accessorFn: (a) => a.countries.map(countryName).sort().join(', '),
      header: t('common.country'),
      meta: { minWidth: 140 },
      cell: ({ row }) =>
        row.original.countries.length > 0 ? (
          <span className="flex flex-wrap items-center gap-1.5 text-xs">
            {row.original.countries.map((code) => (
              <span key={code} title={countryName(code)} className="flex items-center gap-1.5">
                <CountryFlag code={code} />
                {row.original.countries.length === 1 && countryName(code)}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'ownerTeamName',
      header: t('common.team'),
      meta: { minWidth: 120 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.ownerTeamName || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<EarningsAccount>({
      accessorKey: 'revenueShare',
      header: t('publishers.revenueShare'),
      size: 130,
      cell: ({ row }) => <NumberCell value={row.original.revenueShare ?? null} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'directDealRevenueShare',
      header: t('publishers.directDealRevenueShare'),
      size: 160,
      cell: ({ row }) => <NumberCell value={row.original.directDealRevenueShare ?? null} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'politicalAdvertising',
      header: t('publishers.politicalAds'),
      size: 150,
      cell: ({ row }) => <MutedCell value={row.original.politicalAdvertising} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'externalReference',
      header: t('common.externalReference'),
      meta: { minWidth: 140 },
      cell: ({ row }) => <MutedCell value={row.original.externalReference} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'description',
      header: t('common.description'),
      meta: { minWidth: 180 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'address.city',
      header: t('address.city'),
      meta: { minWidth: 130 },
      cell: ({ row }) => <MutedCell value={row.original.address?.city} />,
    }),
    optionalColumn<EarningsAccount>({
      id: 'countries',
      header: t('publishers.countries'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.countries} />,
    }),
    optionalColumn<EarningsAccount>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<EarningsAccount>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={accounts}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No publishers found.'}
      columnsKey="earningsAccounts"
      onRowClick={(row) => navigate(`/publishing/earnings/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
    />
  )
}
